import React, { useState, useCallback, useEffect } from 'react';
import { Button, mergeClasses } from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useFREStyles } from './FirstRunExperience.styles';
import { useModalFocusTrap } from '../../hooks/useModalFocusTrap';
import bannerEmojis from '../../assets/bannerEmojis.svg';
import freStep2 from '../../assets/FREStep2.png';
import freStep3 from '../../assets/FREStep3.png';
import freStep4 from '../../assets/FREStep4.png';

interface FirstRunExperienceProps {
  /** Whether the FRE modal is open */
  isOpen: boolean;
  /** Callback when FRE is closed/completed */
  onClose: () => void;
}

interface StepContent {
  title: string;
  description: string;
  headerImage: string;
  isWelcomeStep?: boolean;
}

const STEPS: StepContent[] = [
  {
    title: 'Welcome to New Hire Hub!',
    description:
      "We're so excited to have you aboard. New Hire Hub will help you understand where you are in your onboarding process. Think of it as your one-stop shop for pre-Day 1 tasks to get you set up for your first day.",
    headerImage: bannerEmojis,
    isWelcomeStep: true,
  },
  {
    title: 'Action needed',
    description:
      'The New Hire Hub highlights any tasks that require your attention. Some tasks can be completed directly within the hub, while others may require monitoring for additional communications or completing tasks through related processes and tools.',
    headerImage: freStep2,
  },
  {
    title: 'Your onboarding journey',
    description:
      "Here, you can see a view only, timeline view of all the onboarding tasks you need to complete before Day 1. You can see what you've completed, what's in progress, and what's upcoming! You can also find helpful resources to prepare you for Day 1.",
    headerImage: freStep3,
  },
  {
    title: 'Need help?',
    description:
      "If you ever get stuck, help is never far away. You can always reach out to AskHR via email to get support from someone at Microsoft. We want to make sure you have the best onboarding experience possible.",
    headerImage: freStep4,
  },
];

export const FirstRunExperience: React.FC<FirstRunExperienceProps> = ({ isOpen, onClose }) => {
  const styles = useFREStyles();
  const [currentStep, setCurrentStep] = useState(0);
  const [stepAnnouncement, setStepAnnouncement] = useState('');
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const backButtonRef = React.useRef<HTMLButtonElement>(null);
  const nextButtonRef = React.useRef<HTMLButtonElement>(null);
  const lastNavigationRef = React.useRef<'back' | 'next' | null>(null);
  useModalFocusTrap(dialogRef, isOpen);

  React.useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    if (lastNavigationRef.current === 'back' && currentStep === 0) {
      nextButtonRef.current?.focus();
    } else if (lastNavigationRef.current === 'back') {
      backButtonRef.current?.focus();
    } else if (lastNavigationRef.current === 'next') {
      nextButtonRef.current?.focus();
    }
  }, [currentStep, isOpen]);

  useEffect(() => {
    if (!isOpen || lastNavigationRef.current === null) {
      return;
    }

    // Clear first so NVDA detects a genuine text mutation, then defer the real
    // announcement a tick past the focus-management effect above. Setting it
    // immediately races with the disabled-button forced blur/refocus that occurs
    // when navigating back to step 1, which causes the assertive alert to be
    // silently dropped instead of announced.
    setStepAnnouncement('');

    const step = STEPS[currentStep];
    const spokenTitle = /[.!?]$/.test(step.title) ? step.title : `${step.title}.`;
    const focusedButtonLabel =
      lastNavigationRef.current === 'next'
        ? currentStep === STEPS.length - 1
          ? 'Done'
          : 'Next'
        : currentStep === 0
          ? 'Next'
          : 'Back';
    const timeoutId = window.setTimeout(() => {
      setStepAnnouncement(
        `Dialog. ${spokenTitle} Step ${currentStep + 1} of ${STEPS.length}. ${step.description} ${focusedButtonLabel} button.`,
      );
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [currentStep, isOpen]);

  // Reset to first step whenever FRE is opened, and ensure Escape works reliably
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setCurrentStep(0);
    setStepAnnouncement('');
    lastNavigationRef.current = null;

    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', onDocumentKeyDown);
    return () => document.removeEventListener('keydown', onDocumentKeyDown);
  }, [isOpen, onClose]);

  const totalSteps = STEPS.length;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const handleBack = useCallback(() => {
    if (!isFirstStep) {
      lastNavigationRef.current = 'back';
      setCurrentStep((prev) => prev - 1);
    }
  }, [isFirstStep]);

  const handleNext = useCallback(() => {
    if (isLastStep) {
      onClose();
    } else {
      lastNavigationRef.current = 'next';
      setCurrentStep((prev) => prev + 1);
    }
  }, [isLastStep, onClose]);

  const handleOverlayClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  const step = STEPS[currentStep];

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="fre-title"
      onClick={handleOverlayClick}
    >
      <div className={styles.modal}>
        <Button
          className={styles.closeButton}
          appearance="subtle"
          aria-label="Close"
          icon={<Dismiss24Regular />}
          onClick={onClose}
        />

        {/* Header with illustration */}
        <div className={mergeClasses(styles.header, step.isWelcomeStep && styles.headerWelcome)}>
          <img
            src={step.headerImage}
            alt=""
            className={mergeClasses(styles.headerImage, step.isWelcomeStep && styles.headerImageWelcome)}
            aria-hidden="true"
          />
        </div>

        {/* Content */}
        <div className={styles.content}>
          <h2 id="fre-title" className={styles.title}>
            {step.title}
          </h2>
          <p className={styles.description}>{step.description}</p>
        </div>

        {/* Footer with navigation */}
        <div className={styles.footer}>
          {/* Back button */}
          <div className={styles.navButtons}>
            <Button
              ref={backButtonRef}
              className={styles.backButton}
              appearance="outline"
              onClick={handleBack}
              disabled={isFirstStep}
            >
              Back
            </Button>
          </div>

          {/* Step indicators */}
          <div
            className={styles.stepIndicators}
            role="progressbar"
            aria-label="Walkthrough progress"
            aria-valuemin={1}
            aria-valuemax={totalSteps}
            aria-valuenow={currentStep + 1}
            aria-valuetext={`Step ${currentStep + 1} of ${totalSteps}`}
          >
            {STEPS.map((_, index) => (
              <span
                key={index}
                className={mergeClasses(
                  styles.stepDot,
                  index < currentStep && styles.stepDotCompleted,
                  index === currentStep && styles.stepDotActive,
                )}
                data-step-state={index < currentStep ? 'completed' : index === currentStep ? 'current' : 'upcoming'}
                aria-hidden="true"
              />
            ))}
          </div>
          <div
            className={styles.liveAnnouncement}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            {stepAnnouncement}
          </div>

          {/* Next/Done button */}
          <div className={styles.navButtons}>
            <Button
              ref={nextButtonRef}
              className={styles.nextButton}
              appearance="primary"
              onClick={handleNext}
            >
              {isLastStep ? 'Done' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
